import React, { PureComponent } from 'react';
import { Translate } from 'react-localize-redux';
import { isLoaded } from 'react-redux-firebase';
import dayjs from 'dayjs';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { REVIEW_CHECKOUT_STEP } from '../../analytics';
import Heading, { SubHeading } from '../../components/heading';
import getPaymentLogo from '../../utilities/get-payment-logo';
import Button from '../button';
import { IcoMoonSpinner4 } from '../ico-moon';
import Indicator from '../indicator';
import Detail from './detail';
import SubList from './sub-list';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const FlexedDetailContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 40% 60%;
  justify-content: center;
  justify-items: baseline;
  grid-row-gap: 1.5em;
`;

export const PaymentIcon = styled.img`
  width: 2em;
  margin-left: 1em;
`;

const Scoller = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const SmallText = styled.div`
  margin-top: 0.5em;
  font-size: 0.8em;
`;

const Background = styled.div`
  background: #171717;
  color: white;
  padding: 2em;
`;

const Total = styled.div`
  font-weight: bold;
  font-size: 1.5em;
`;

const Submit = styled(Button)`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80%;
`;

/**
 * Presentational component to review the payment before placing purchase
 */
class ReviewPayment extends PureComponent {
  state = {
    submitting: false
  };

  componentDidMount() {
    window.dataLayer.push({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: { step: REVIEW_CHECKOUT_STEP }
        }
      }
    });
  }

  handleSubmit = () => {
    this.setState({
      submitting: true
    });
    this.props.onSubmit();
  };

  displayPaymentInfo() {
    if (!this.props.total) {
      return;
    }
    let info;
    let logo;
    switch (this.props.paymentType) {
      case 'card': {
        const card = this.props.card;
        if (!card || !card.value || !card.value.id) {
          info = '...';
          break;
        }
        logo = getPaymentLogo('card', card.value.brand.toLowerCase());
        info = `${card.value.last4}`;
        break;
      }
      case 'konbini': {
        logo = getPaymentLogo('konbini', this.props.paymentId);
        break;
      }
      case 'browser': {
        logo = getPaymentLogo('browser', this.props.paymentRequestVendor);
        break;
      }
      default:
        break;
    }
    return (
      <Detail pathname="/checkout/payment-sources" title="paymentInfo">
        <FlexedDetailContent>
          {info}
          <PaymentIcon src={logo} />
        </FlexedDetailContent>
      </Detail>
    );
  }

  render() {
    const {
      game,
      gameId,
      locale,
      timeslot,
      total,
      reduction,
      tickets,
      coupons,
      referral
    } = this.props;
    if (!isLoaded(timeslot)) {
      return <Indicator spin />;
    }
    const tooManyCoupons = coupons.length + Number(!!referral) > tickets;
    if (tooManyCoupons || (total && !this.props.paymentType)) {
      navigate(`/checkout/payment-sources${this.props.location.search}`);
      return null;
    }
    const date = dayjs.unix(timeslot.time).locale(locale);
    const isHolidaySlot = timeslot.isHoliday || [0, 6].indexOf(date.day()) > -1;
    return (
      <Container>
        <Heading>
          <Translate id="checkout.review.title" />
        </Heading>
        <SubHeading>
          <Translate id="checkout.review.subtitle" />
        </SubHeading>
        <Scoller>
          <Background>
            <Wrapper>
              <Detail title="game">{game.name}</Detail>
              <Detail
                pathname={`/games/${this.props.gameId}/calendar/`}
                title="date"
              >
                {date.format('YYYY年 M月 D日 (ddd)')}
              </Detail>
              <Detail
                pathname={`/games/${this.props.gameId}/schedule/`}
                title="time"
              >
                <span>{date.format('HH:mm ')}&nbsp;</span>
                <span>
                  {game && game.gameDuration && `(${game.gameDuration})`}
                </span>
              </Detail>
              {game /* && game.address */ && (
                <Detail title="place">
                  <div>
                    <Translate
                      id={`games.${gameId}.place`}
                      options={{ renderInnerHtml: true }}
                    />
                  </div>
                  {/* <div dangerouslySetInnerHTML={{ __html: game.address }} /> */}
                  <SmallText>
                    <Translate id="checkout.review.placeDetailsInfo" />
                  </SmallText>
                </Detail>
              )}
              {this.displayPaymentInfo()}
              <Detail pathname="/checkout/tickets" title="tickets">
                <div>{tickets}</div>
              </Detail>
              {(!!coupons.length || referral) && (
                <Detail pathname="/checkout/payment-sources" title="coupons">
                  {coupons.map(({ data }, index) => (
                    <div key={index}>{data.discount.name}</div>
                  ))}
                  {!!referral && <div>{referral.name}</div>}
                </Detail>
              )}
              <Detail title="subtotal">
                <SubList
                  amount={tickets * timeslot.amount}
                  counter="ticketCounter"
                  name={isHolidaySlot ? 'holidayTicket' : 'weekdayTicket'}
                  quantity={tickets}
                />
                {!!reduction && <SubList amount={-reduction} name="coupons" />}
              </Detail>
              <Detail title="total">
                <Total> {total}円 </Total>
              </Detail>
            </Wrapper>
          </Background>
        </Scoller>
        <Submit disabled={this.state.submitting} onClick={this.handleSubmit}>
          {this.state.submitting ? (
            <IcoMoonSpinner4 className="spin" size="1.5em" />
          ) : (
            <Translate id="checkout.review.pay" />
          )}
        </Submit>
      </Container>
    );
  }
}

ReviewPayment.propTypes = {
  gameId: PropTypes.string.isRequired,
  game: PropTypes.object,
  paymentType: PropTypes.string,
  paymentId: PropTypes.any,
  referral: PropTypes.object,
  paymentRequestVendor: PropTypes.string,
  locale: PropTypes.string.isRequired,
  timeslot: PropTypes.object.isRequired,
  amount: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  reduction: PropTypes.number.isRequired,
  tickets: PropTypes.number.isRequired,
  coupons: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func.isRequired,
  card: PropTypes.object
};

ReviewPayment.defaultProps = {
  card: null,
  coupons: []
};

export default ReviewPayment;
